import api from '@/lib/api';

export interface ChangePasswordRequest {
  current_password: string;
  new_password: string;
  confirm_password: string;
}

export interface Invoice {
  id: string;
  invoice_number: string;
  amount: number;
  paid_amount?: number;
  status: 'pending' | 'paid' | 'overdue' | 'cancelled';
  due_date: string;
  paid_at?: string;
  period_start: string;
  period_end: string;
  description?: string;
  pdf_url?: string;
  payment_url?: string;
  created_at: string;
  plan_name?: string;
  billing_cycle?: string;
}

export interface Setup2FAResponse {
  secret: string;
  qr_code: string;
  backup_codes: string[];
}

/** Quanto se paga hoje para assinar num ciclo e quando vem a próxima cobrança. */
export interface TrialActivationQuote {
  billing_cycle: 'monthly' | 'yearly';
  amount: number;
  /** Data de negócio (`AAAA-MM-DD`). */
  next_charge_date: string;
}

/** Situação do período de teste — regra decidida no backend; a tela só exibe. */
export interface TrialSummary {
  /** Último dia do teste (data de negócio, `AAAA-MM-DD`). */
  trial_end: string;
  days_remaining: number;
  /** Teste já vencido com o rebaixamento adiado (cobrança da assinatura em aberto). */
  has_ended: boolean;
  /** Tom do aviso: `ending` nos últimos dias, `last_day` no último. */
  notice_level: 'info' | 'ending' | 'last_day';
  /** Plano para onde o assinante vai ao fim do teste sem pagamento. */
  plan_after_trial_name: string | null;
  activation_quotes: TrialActivationQuote[];
}

export interface UserSubscription {
  id: string;
  plan_id: string;
  plan_name: string | null;
  /** valores conhecidos: 'individual' | 'family' */
  account_type?: string | null;
  max_users_per_account?: number;
  status: string;
  billing_cycle: string;
  current_period_start: string | null;
  current_period_end: string | null;
  trial_end: string | null;
  is_active: boolean;
  /** True enquanto houver aviso de fim de trial pendente (migração on-access para o gratuito). */
  trial_just_ended?: boolean;
  /** Plano atual é gratuito (não exige dados de cobrança / "Complete seu cadastro"). */
  is_free_plan?: boolean;
  applied_coupon?: AppliedCoupon | null;
  /** UUID da assinatura na VIO Pay — quando presente, banner/guards consomem direto da VIO. */
  vio_subscription_uuid?: string | null;
  /** Dias para suspender o acesso após vencimento. Vem da VIO Pay (não é hardcoded). `null` => sem regra; `0` => suspende imediatamente. */
  suspension_days_overdue?: number | null;
  /** Dias para cancelamento automático após vencimento. */
  auto_cancel_days_overdue?: number | null;
  /** Cancelamento automático ativado pela VIO. */
  auto_cancel_enabled?: boolean | null;
  /** Status remoto na VIO Pay (`ACTIVE`, `PAUSED`, `SUSPENDED`, `CANCELED`). */
  remote_status?: string | null;
  /** Próxima data de cobrança (ISO-8601). */
  next_due_date?: string | null;
  /** Troca de plano aguardando pagamento: id do plano-alvo (null quando não há). */
  pending_plan_id?: string | null;
  /** Nome do plano-alvo da troca pendente. */
  pending_plan_name?: string | null;
  /** Ciclo pedido na troca pendente ('monthly' | 'yearly'). */
  pending_billing_cycle?: string | null;
  /** Quando a troca pendente foi pedida (ISO-8601). */
  pending_requested_at?: string | null;
  /** UUID do receivable da troca pendente — âncora da cobrança do upgrade. */
  pending_receivable_uuid?: string | null;
  /** URL de checkout da cobrança da troca pendente, se em aberto. */
  pending_checkout_url?: string | null;
  /** Inadimplência da assinatura vigente — regra única no backend (TK-1623). */
  payment_delinquency?: PaymentDelinquency | null;
  /** Último dia da vigência do anual quitado (data de negócio, `AAAA-MM-DD`): até
   * lá não se migra para o mensal nem para o gratuito. `null` = sem bloqueio.
   * Regra decidida no backend; a tela só exibe. */
  annual_lock_until?: string | null;
  /** `false` para membro convidado da conta: só o titular troca ou cancela o plano. */
  is_billing_owner?: boolean;
  /** Período de teste vigente (`null` fora do teste). */
  trial?: TrialSummary | null;
  /** Cancelamento da assinatura (None no plano gratuito). */
  cancellation?: CancellationSummary | null;
}

/** Cobrança vencida da assinatura vigente, com os dias de atraso calculados pelo backend. */
export interface DelinquentCharge {
  id: string;
  invoice_number: string | null;
  amount: number;
  /** Data de vencimento (YYYY-MM-DD). */
  due_date: string;
  payment_url: string | null;
  plan_name: string | null;
  days_overdue: number;
}

/**
 * Situação de inadimplência servida por `GET /billing/my-subscription`. O
 * backend só considera as cobranças da assinatura vigente — cobranças órfãs de
 * assinaturas anteriores nunca bloqueiam nem geram aviso.
 */
export interface PaymentDelinquency {
  /** Acesso bloqueado: assinatura SUSPENDED ou cobrança acima do limite. */
  is_blocked: boolean;
  /** Dias de atraso tolerados antes do bloqueio (`0` = já no 1º dia). */
  suspension_threshold_days: number;
  /** Pior cobrança acima do limite (tela "Pagamento Pendente"). */
  blocking_charge: DelinquentCharge | null;
  /** Pior cobrança vencida ainda dentro do limite (tarja de aviso). */
  warning_charge: DelinquentCharge | null;
  /** As cobranças não puderam ser lidas (VIO indisponível). */
  charges_unavailable: boolean;
}

/** Principal motivo do cancelamento (categoria fixa do backend). */
export type CancellationReason =
  | 'budget'
  | 'free_plan_enough'
  | 'low_usage'
  | 'missing_features'
  | 'technical_issues'
  | 'switching_tool'
  | 'other';

/** Cancelamento da assinatura (chamado 7116): regra decidida no backend. */
export interface CancellationSummary {
  /** Já foi pedido: os recursos seguem até `effective_date`. */
  scheduled: boolean;
  /** Fim do período contratado (ou do teste), `AAAA-MM-DD`. */
  effective_date: string | null;
  reason_category: CancellationReason | null;
  /** `downgrade`: ida ao gratuito pedida no teste, que fica para o fim dele. */
  kind: 'cancellation' | 'downgrade';
}

/** Situação do plano para o ciente do "Excluir conta" (regra do backend). */
export type AccountDeletionSituation = 'paid' | 'trial' | 'free';

export interface AccountDeletionSummary {
  /** Só o titular exclui (nunca no acesso administrativo). */
  can_delete: boolean;
  situation: AccountDeletionSituation | null;
  /** Até quando o plano iria (`AAAA-MM-DD`), no pago e no teste. */
  access_until: string | null;
  /** Membros que também perdem o acesso e têm os dados apagados. */
  members_count: number;
}

/** Motivos do "Excluir conta" (categorias do backend, diferentes das do cancelamento). */
export type AccountDeletionReason =
  | 'privacy'
  | 'no_longer_use'
  | 'switching_tool'
  | 'duplicate_account'
  | 'start_over'
  | 'bad_experience'
  | 'other';

export interface DeleteAccountPayload {
  acknowledged: boolean;
  reason_category?: AccountDeletionReason;
  reason?: string;
}

export interface CancelSubscriptionResponse {
  message: string;
  /** False quando o período já tinha acabado e a conta passou ao gratuito na hora. */
  cancellation_scheduled: boolean;
  effective_date: string | null;
}

export interface ChangePlanPayload {
  plan_id: string;
  billing_cycle: 'monthly' | 'yearly';
}

export interface ChangePlanResponse {
  message: string;
  /** 'pending_payment' (alvo pago aguardando pagamento) | 'pending_cancelled' |
   * 'scheduled' (ida ao gratuito no teste, para o fim dele). */
  status?: string;
  /** Com `scheduled`: quando a troca vale (`AAAA-MM-DD`). */
  effective_date?: string | null;
  plan_id: string;
  plan_name?: string;
  billing_cycle?: string;
  pending_plan_id?: string | null;
  pending_billing_cycle?: string | null;
  vio_subscription_uuid: string | null;
  /** URL do checkout VIO da fatura aberta, se houver. */
  payment_url: string | null;
}

export interface DowngradeToFreeResponse {
  message: string;
  plan_id: string;
  plan_name?: string;
}

export interface CancelPendingPlanChangeResponse {
  message: string;
  /** False quando não havia troca pendente (chamada idempotente). */
  changed: boolean;
  plan_id: string;
  plan_name?: string | null;
}

export interface AppliedCoupon {
  code: string;
  /** valores conhecidos: 'percentage' | 'fixed' */
  discount_type: string;
  discount_value: number;
  /** valores conhecidos: 'first_invoice' | 'months' | 'until_end' */
  validity_type: string;
  validity_months?: number | null;
  applied_at?: string;
}

export interface ApplySubscriptionCouponResponse {
  message: string;
  coupon: AppliedCoupon;
  applied_invoices: Array<{
    id: string;
    invoice_number: string;
    original_amount: number;
    discount_amount: number;
    new_amount: number;
  }>;
  total_discount_applied: number;
}

export interface FamilyMember {
  id: string;
  name: string;
  email: string;
  role: string;
  is_owner: boolean;
  is_active: boolean;
  created_at: string;
  /** `null` = acesso liberado (aceitou o convite ou é anterior aos convites). */
  invitation_status?: 'pending' | 'expired' | null;
}

export interface FamilyMembersListResponse {
  can_manage_users: boolean;
  max_users: number | null;
  users_count: number;
  users: FamilyMember[];
}

/**
 * Convite e edição de membro. Sem senha de propósito: quem a define é o próprio
 * membro, no link do convite ou pelo "Esqueceu a senha?" da tela de login.
 */
export interface FamilyMemberPayload {
  name: string;
  email: string;
}

export interface AdminAccessPermissionResponse {
  allow_admin_access: boolean;
}

class AccountService {
  // Password
  async changePassword(data: ChangePasswordRequest): Promise<{ message: string }> {
    const response = await api.post('/auth/change-password', data);
    return response.data;
  }

  // Invoices - Get user's own invoices
  async getMyInvoices(): Promise<Invoice[]> {
    const response = await api.get('/billing/my-invoices');
    return response.data;
  }

  // 2FA Setup
  async setup2FA(): Promise<Setup2FAResponse> {
    const response = await api.post('/auth/2fa/setup');
    return response.data;
  }

  // 2FA Enable
  async enable2FA(code: string): Promise<{ message: string }> {
    const response = await api.post('/auth/2fa/enable', { code });
    return response.data;
  }

  // 2FA Disable
  async disable2FA(password: string, code: string): Promise<{ message: string }> {
    const response = await api.post('/auth/2fa/disable', { password, code });
    return response.data;
  }

  // Subscription - Get current user's active subscription
  async getMySubscription(): Promise<UserSubscription | null> {
    const response = await api.get('/billing/my-subscription');
    return response.data;
  }

  async applyCouponToMySubscription(code: string): Promise<ApplySubscriptionCouponResponse> {
    const response = await api.post('/billing/my-subscription/apply-coupon', { code });
    return response.data;
  }

  /** Cancela a assinatura (não a conta): os recursos seguem até o fim do
   * período contratado e depois a conta passa ao plano gratuito. O motivo é
   * obrigatório; o comentário, opcional. */
  async cancelMySubscription(
    reasonCategory: CancellationReason,
    reason: string | undefined,
  ): Promise<CancelSubscriptionResponse> {
    const response = await api.post('/billing/my-subscription/cancel', {
      reason_category: reasonCategory,
      reason,
    });
    return response.data;
  }

  /** O que a tela "Excluir conta" precisa para montar o ciente. */
  async getAccountDeletion(): Promise<AccountDeletionSummary> {
    const response = await api.get('/users/me/account-deletion');
    return response.data;
  }

  /** Exclui a conta: cancela a assinatura na VIO e apaga os dados na hora. */
  async deleteMyAccount(payload: DeleteAccountPayload): Promise<void> {
    await api.post('/users/me/account-deletion', payload);
  }

  /** Troca o plano da assinatura ativa via PUT in-place na VIO Pay (não recria assinatura).
   * Alvo pago fica PENDENTE até o pagamento confirmar (`status: 'pending_payment'`). */
  async changeMyPlan(payload: ChangePlanPayload): Promise<ChangePlanResponse> {
    const response = await api.post('/billing/my-subscription/change-plan', payload);
    return response.data;
  }

  /** Descarta a troca de plano aguardando pagamento ("Continuar no plano atual"). */
  async cancelPendingPlanChange(): Promise<CancelPendingPlanChangeResponse> {
    const response = await api.post('/billing/my-subscription/cancel-pending-change');
    return response.data;
  }

  /** Move a assinatura ativa para o plano gratuito (ação "Plano Gratuito" do fim do trial). */
  async downgradeToFree(): Promise<DowngradeToFreeResponse> {
    const response = await api.post('/billing/my-subscription/downgrade-to-free');
    return response.data;
  }

  /** Descarta o aviso de fim de trial (limpa o flag persistido no backend). */
  async ackTrialEnded(): Promise<{ ok: boolean }> {
    const response = await api.post('/billing/my-subscription/ack-trial-ended');
    return response.data;
  }

  /** URL do PDF da fatura — backend faz redirect para `/fatura/{uuid}/pdf` da VIO. */
  getMyInvoicePdfUrl(invoiceId: string): string {
    const baseUrl = (api.defaults.baseURL || '').replace(/\/$/, '');
    return `${baseUrl}/billing/my-invoices/${invoiceId}/pdf`;
  }

  // Admin account access permission
  async updateAdminAccessPermission(allowAdminAccess: boolean): Promise<AdminAccessPermissionResponse> {
    const response = await api.put('/users/me/admin-access-permission', {
      allow_admin_access: allowAdminAccess,
    });
    return response.data;
  }

  // Family plan user management
  async getFamilyMembers(): Promise<FamilyMembersListResponse> {
    const response = await api.get('/users/me/family-members');
    return response.data;
  }

  async createFamilyMember(payload: FamilyMemberPayload): Promise<{ message: string; user: FamilyMember }> {
    const response = await api.post('/users/me/family-members', payload);
    return response.data;
  }

  /** Gera novo link de convite (o anterior deixa de valer) e reenvia o e-mail. */
  async resendFamilyMemberInvite(memberId: string): Promise<{ message: string; user: FamilyMember }> {
    const response = await api.post(`/users/me/family-members/${memberId}/resend-invite`);
    return response.data;
  }

  async updateFamilyMember(memberId: string, payload: FamilyMemberPayload): Promise<{ message: string; user: FamilyMember }> {
    const response = await api.put(`/users/me/family-members/${memberId}`, payload);
    return response.data;
  }

  async deleteFamilyMember(memberId: string): Promise<{ message: string }> {
    const response = await api.delete(`/users/me/family-members/${memberId}`);
    return response.data;
  }
}

export const accountService = new AccountService();
export default accountService;
